import ms from 'ms'
import React, {ComponentType, useCallback, useEffect, useMemo, useState} from 'react'
import {FormattedDate, FormattedMessage} from 'react-intl'
import ChevronLeft from '../../assets/icons/ChevronLeft.svg'
import ChevronRight from '../../assets/icons/ChevronRight.svg'
import {numberTo2Digits} from '../../helpers/coreUtil'
import {sampleDaysInWeek} from '../../helpers/dateUtil'
import {usePrev} from '../hocs/hooks'
import styles from './Calendar.scss'

export enum CalendarViewType {
  van,
  station,
  default
}
export interface IDateComponentProps {
  date: Date
  month: Date
  onSelect?: () => any
  disabled: boolean
  today: Date
  selected: boolean
  calendarViewType?: CalendarViewType
}
const DefaultDateComponent = (
  {
    selected,
    today,
    date,
    month,
    onSelect,
    disabled,
    calendarViewType = CalendarViewType.default
  }: IDateComponentProps
) => <div
  className={[
    'text-center',
    calendarViewType === CalendarViewType.default && 'p-2',
    calendarViewType !== CalendarViewType.default && date.getMonth() !== month.getMonth() && 'd-none',
    (disabled || date.getMonth() !== month.getMonth()) && styles.textMuted,
    disabled && styles.disabled,
    !disabled && date.getMonth() === month.getMonth() && 'c-pointer',
    !disabled && date.getMonth() === month.getMonth() && (
      calendarViewType !== CalendarViewType.default ? [
        styles.canBookCustom,
        calendarViewType === CalendarViewType.station
          ? styles.stationColor
          : styles.vanColor
      ].join(' ') : styles.canBook
    ),
    calendarViewType === CalendarViewType.default && styles.calendarDate,
    selected && styles.selected,
    calendarViewType === CalendarViewType.default && today.getTime() === date.getTime() && 'text-danger font-weight-bold'
  ].filter(Boolean).join(' ')}
  onClick={(disabled || date.getMonth() !== month.getMonth()) ? undefined : onSelect}
>
  {calendarViewType !== CalendarViewType.default ? date.getDate() : numberTo2Digits(date.getDate())}
</div>
DefaultDateComponent.displayName = 'DefaultDateComponent'

const defaultIsDisabled = () => false
const Calendar = <T, >(
  {
    month: propMonth,
    DateComponent = DefaultDateComponent,
    onChange,
    isDisabled = defaultIsDisabled,
    props = {},
    onMonthChange,
    value,
    minYear,
    maxYear,
    showUtilButtons = false,
    calendarViewType = CalendarViewType.default,
  }: {
    month?: Date
    DateComponent?: ComponentType<IDateComponentProps & Partial<T>>
    onChange?: (date: Date, e: any) => any
    isDisabled?: (date: Date) => boolean
    props?: Partial<T>
    onMonthChange?: (date: Date) => any
    value?: Date,
    minYear: number
    maxYear: number
    showUtilButtons?: boolean
    calendarViewType?: CalendarViewType
  }) => {
  const initialDateStartDay = value && new Date(value)
  if (initialDateStartDay) initialDateStartDay.setHours(0, 0, 0, 0)
  const initialMonth = propMonth ? new Date(propMonth) : new Date(Date.now())
  initialMonth.setDate(1)
  initialMonth.setHours(0, 0, 0, 0)
  const [month, setMonth] = useState(initialMonth)
  const prevInitialMonth = usePrev(initialMonth)
  useEffect(() => void (initialMonth.getTime() !== prevInitialMonth.getTime() && setMonth(initialMonth)),
    [initialMonth, prevInitialMonth])
  const makeMonthNav = useCallback((nMonth: number) => () => {
    const cloned = new Date(month)
    cloned.setMonth(cloned.getMonth() + nMonth)
    if (minYear > cloned.getFullYear() || maxYear < cloned.getFullYear()) return
    setMonth(cloned)
    onMonthChange?.(cloned)
  }, [month, onMonthChange])
  const navPrevMonth = useMemo(() => makeMonthNav(-1), [makeMonthNav])
  const navNextMonth = useMemo(() => makeMonthNav(1), [makeMonthNav])

  const makeYearNav = useCallback((nYear: number) => () => {
    const cloned = new Date(month)
    cloned.setFullYear(cloned.getFullYear() + nYear)
    setMonth(cloned)
    onMonthChange?.(cloned)
  }, [month, onMonthChange])

  const nextMonth = new Date(month)
  nextMonth.setMonth(nextMonth.getMonth() + 1)
  const ndays = Math.round((nextMonth.getTime() - month.getTime()) / ms('1 day'))
  const nweeks = Math.ceil((ndays + month.getDay()) / 7)
  const today = new Date(Date.now())
  today.setHours(0, 0, 0, 0)
  const navThisMonth = () => {
    const cloned = new Date(today)
    cloned.setDate(1)
    setMonth(cloned)
    onMonthChange?.(cloned)
  }
  const navSelectedMonth = () => {
    if (value) {
      const m = value && new Date(value)
      m.setHours(0, 0, 0, 0)
      m.setDate(1)
      setMonth(m)
      onMonthChange?.(m)
    }
  }
  return <div className={['d-inline-block no-text-break',
    calendarViewType === CalendarViewType.default && 'p-2',
    calendarViewType !== CalendarViewType.default && 'w-100 h-100'
  ].filter(Boolean).join(' ')}>
    <div className={['d-flex form-group', calendarViewType !== CalendarViewType.default && styles.vanHeader].filter(Boolean).join(' ')}>
      <div className={['flex-grow-1 d-flex align-items-center justify-content-between'].filter(Boolean).join(' ')}>
        <ChevronLeft className={['fa c-pointer', styles.arrow].filter(Boolean).join(' ')} onClick={navPrevMonth}/>
        <select
          className="custom-select w-75"
          value={month.getFullYear()}
          onChange={({target: {value: newVal}}) => {
            makeYearNav(parseInt(newVal) - month.getFullYear())()
          }}
        >
          {[...Array(maxYear + 1).keys()].slice(minYear).map(
            i => <FormattedDate key={i} value={new Date(today).setFullYear(i)} year="numeric">
              {message => <option value={i}>{message}</option>}
            </FormattedDate>
          )}
        </select>
      </div>
      <div className="h-100 mx-1"/>
      <div className="flex-grow-1 d-flex justify-content-between align-items-center">
        <select
          className="custom-select w-75"
          value={month.getMonth()}
          onChange={({target: {value: newVal}}) => makeMonthNav(parseInt(newVal) - month.getMonth())()}
        >
          {[...Array(12).keys()].map(i => <FormattedDate key={i} value={new Date(today).setMonth(i, 1)} month="long">
              {message => <option value={i}>{message}</option>}
            </FormattedDate>
          )}
        </select>
        <ChevronRight className={['fa c-pointer', styles.arrow].filter(Boolean).join(' ')} onClick={navNextMonth}/>
      </div>
    </div>
    <table className={[
      calendarViewType !== CalendarViewType.default && styles.carStationTable,
      calendarViewType !== CalendarViewType.default && 'w-100'
    ].filter(Boolean).join(' ')}>
      <thead>
      <tr>
        {sampleDaysInWeek.map((wd, day) => <th key={day}>
          <div className="text-center">
            <FormattedDate value={wd} weekday="short"/>
          </div>
        </th>)}
      </tr>
      </thead>
      <tbody>
      {[...Array(nweeks).keys()].map(week => <tr key={week}>
        {[...Array(7).keys()].map(ind => {
          const date = new Date(month.getTime() + (week * 7 + ind - month.getDay()) * ms('1 day'))
          return <td key={ind}>
            <DateComponent date={date}
                           selected={!!initialDateStartDay && initialDateStartDay.getTime() === date.getTime()}
                           today={today}
                           month={month}
                           onSelect={(e) => {
                             if (
                               onChange && date.getFullYear() >= minYear && date.getFullYear() <= maxYear
                             ) onChange(date, e)
                           }}
                           disabled={isDisabled(date)}
                           calendarViewType={calendarViewType}
                           {...props}
            />
          </td>
        })}
      </tr>)}
      </tbody>
    </table>
    {showUtilButtons && <div className="row">
        <div className="col-6">
          <button className="btn btn-light w-100" type="button" onClick={navThisMonth}>
            <FormattedMessage id="calendar.thisMonth" defaultMessage="This month"/>
          </button>
        </div>
        <div className="col-6">
          <button className="btn btn-light w-100"
                  type="button"
                  disabled={!value}
                  onClick={navSelectedMonth}>
            <FormattedMessage id="calendar.selected" defaultMessage="Selected day"/>
          </button>
        </div>
      </div>
    }
  </div>
}
Calendar.displayName = 'Calendar'
export default Calendar
